import { CONSENT_VERSION, type ConsentDecision, type ConsentSource } from "./constants"

function anonymousId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }

  return `anon-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
}

type ConsentEvent = "consent_accepted" | "consent_rejected" | "consent_settings_opened"

/**
 * Reports over plain HTTP instead of posthog-js, which never initialises for a
 * visitor who rejects — the reason decline rate was previously unmeasurable.
 * Sends no identifier and writes nothing to the device, so it runs regardless of
 * what the visitor chose.
 *
 * Every event carries a fresh id, so these are counts to be compared in
 * aggregate, never joined per person.
 *
 * No-ops when the app has no PostHog credentials configured.
 */
function report(event: ConsentEvent, source: ConsentSource): void {
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_API_HOST

  if (!apiKey || !apiHost) {
    return
  }

  const url = `${apiHost.replace(/\/$/, "")}/i/v0/e/`
  const payload = JSON.stringify({
    api_key: apiKey,
    event,
    properties: {
      distinct_id: anonymousId(),
      $process_person_profile: false,
      $current_url: window.location.href,
      consent_version: CONSENT_VERSION,
      consent_source: source,
    },
  })

  try {
    /* A rejection reloads the page immediately after this call. sendBeacon is
       built for sends that race an unload; keepalive fetch is the fallback for
       browsers that refuse the beacon. Losing this would undercount exactly the
       number it exists to measure. */
    const queued = navigator.sendBeacon?.(url, new Blob([payload], { type: "application/json" }))

    if (queued) {
      return
    }

    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: payload,
    }).catch(() => {})
  } catch {
    // A blocked or failed beacon must never break the banner.
  }
}

export function reportConsentDecision(decision: ConsentDecision, source: ConsentSource): void {
  report(decision === "accepted" ? "consent_accepted" : "consent_rejected", source)
}

/**
 * Opening the settings is the only route to a rejection from the banner, so it
 * is the denominator that says whether declines there are deliberate. The
 * account form has no equivalent step — its toggle is simply visible.
 */
export function reportConsentSettingsOpened(): void {
  report("consent_settings_opened", "banner")
}
